'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    deleteAllNotifications,
    getNotificationCategories,
    getNotificationGroups,
    markAllAsRead,
    NotificationCategory,
    NotificationGroup,
    NotificationQuery,
    NotificationType,
    NOTIFICATION_TYPE_LABELS,
} from '@/lib/api/notifications'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdDeleteSweep,
    MdNotifications,
    MdSearch,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import NotificationCategoryMenu, {
    CategorySelection,
} from './notification-category-menu'
import NotificationGroupRow from './notification-group-row'

const GROUPS_PER_PAGE = 20

export default function NotificationsPage() {
    const navigate = useNavigate()

    const [groups, setGroups] = useState<NotificationGroup[]>([])
    const [categories, setCategories] = useState<NotificationCategory[]>([])
    const [loading, setLoading] = useState(true)
    const [loadingCategories, setLoadingCategories] = useState(true)

    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [unreadCount, setUnreadCount] = useState(0)
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [filterUnreadOnly, setFilterUnreadOnly] = useState(false)
    const [filterType, setFilterType] = useState<NotificationType | ''>('')
    const [category, setCategory] = useState<CategorySelection>({})

    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const loadGroups = useCallback(async () => {
        setLoading(true)
        try {
            const query: NotificationQuery = {
                page,
                limit: GROUPS_PER_PAGE,
            }
            if (filterUnreadOnly) query.unreadOnly = true
            if (filterType) query.type = filterType
            if (category.module) query.module = category.module
            if (category.resource) query.resource = category.resource
            if (debouncedSearch) query.search = debouncedSearch

            const response = await getNotificationGroups(query)
            if (response.success) {
                setGroups(response.result.groups)
                setUnreadCount(response.result.unreadCount)
                setTotal(response.result.total)
                setTotalPages(response.result.totalPages)
            } else {
                toast.error(response.error || 'Erro ao carregar notificações')
            }
        } catch {
            toast.error('Erro ao carregar notificações')
        } finally {
            setLoading(false)
        }
    }, [
        page,
        filterUnreadOnly,
        filterType,
        category.module,
        category.resource,
        debouncedSearch,
    ])

    // A busca vai ao servidor: com a paginação por grupo, filtrar só o que está
    // carregado esconderia os grupos que casam nas páginas seguintes.
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim())
            setPage(1)
        }, 350)
        return () => clearTimeout(timer)
    }, [searchTerm])

    const loadCategories = useCallback(async () => {
        try {
            const response = await getNotificationCategories()
            if (response.success) {
                setCategories(response.result)
            }
        } catch {
            // O menu é auxiliar: se falhar, a lista continua utilizável.
        } finally {
            setLoadingCategories(false)
        }
    }, [])

    useEffect(() => {
        loadGroups()
    }, [loadGroups])

    useEffect(() => {
        loadCategories()
    }, [loadCategories])

    /** Recarrega lista e contadores após uma ação em lote. */
    const refresh = useCallback(() => {
        loadGroups()
        loadCategories()
    }, [loadGroups, loadCategories])

    const handleSelectCategory = (selection: CategorySelection) => {
        setCategory(selection)
        setPage(1)
    }

    const categoryLabels = useMemo(() => {
        const map = new Map<string, { label: string; resources: Map<string, string> }>()
        for (const c of categories) {
            map.set(c.key, {
                label: c.label,
                resources: new Map(c.resources.map(r => [r.key, r.label])),
            })
        }
        return map
    }, [categories])

    const totalNotifications = useMemo(
        () => categories.reduce((sum, c) => sum + c.total, 0),
        [categories]
    )
    const totalUnread = useMemo(
        () => categories.reduce((sum, c) => sum + c.unread, 0),
        [categories]
    )

    const handleMarkAllAsRead = async () => {
        const response = await markAllAsRead()
        if (response.success) {
            toast.success('Todas as notificações foram marcadas como lidas')
            refresh()
        }
    }

    const confirmDeleteAll = async () => {
        setDeleting(true)
        try {
            const response = await deleteAllNotifications()
            if (response.success) {
                toast.success('Todas as notificações foram excluídas')
                refresh()
            } else {
                toast.error(response.error || 'Erro ao excluir notificações')
            }
        } catch {
            toast.error('Erro ao excluir notificações')
        } finally {
            setDeleting(false)
            setShowDeleteAllModal(false)
        }
    }

    const hasFilters =
        !!searchTerm || filterUnreadOnly || !!filterType || !!category.module

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdNotifications className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Notificações
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'grupo' : 'grupos'}
                            {unreadCount > 0 && (
                                <span className="ml-1 text-secondary font-medium">
                                    ({unreadCount} não lidas)
                                </span>
                            )}
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={() => navigate('/notifications/new')}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Nova Notificação</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col gap-4">
                    <NotificationCategoryMenu
                        categories={categories}
                        active={category}
                        onSelect={handleSelectCategory}
                        totalAll={totalNotifications}
                        unreadAll={totalUnread}
                        loading={loadingCategories}
                    />

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1 border-t border-base-300/50">
                        <div className="flex-1 relative pt-3 sm:pt-0">
                            <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Buscar por tipo de notificação..."
                                className="input input-bordered w-full pl-10"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <select
                                className="select select-bordered"
                                value={filterType}
                                onChange={e => {
                                    setFilterType(
                                        e.target.value as NotificationType | ''
                                    )
                                    setPage(1)
                                }}
                            >
                                <option value="">Todos os tipos</option>
                                {Object.entries(NOTIFICATION_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>

                            <label className="label cursor-pointer gap-2">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-secondary checkbox-sm"
                                    checked={filterUnreadOnly}
                                    onChange={e => {
                                        setFilterUnreadOnly(e.target.checked)
                                        setPage(1)
                                    }}
                                />
                                <span className="label-text whitespace-nowrap">
                                    Não lidas
                                </span>
                            </label>
                        </div>
                    </div>

                    {groups.length > 0 && (
                        <div className="flex items-center justify-between">
                            {unreadCount > 0 ? (
                                <button
                                    className="btn btn-ghost btn-sm gap-1"
                                    onClick={handleMarkAllAsRead}
                                >
                                    <MdCheck size={16} />
                                    Marcar todas como lidas
                                </button>
                            ) : (
                                <span />
                            )}
                            <button
                                className="btn btn-ghost btn-sm text-error gap-1"
                                onClick={() => setShowDeleteAllModal(true)}
                            >
                                <MdDeleteSweep size={16} />
                                Limpar todas
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {loading && groups.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                    <div className="loading loading-spinner loading-lg text-secondary" />
                    <p className="text-base-content/60 text-sm">
                        Carregando notificações...
                    </p>
                </div>
            ) : groups.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdNotifications
                        size={64}
                        className="mx-auto mb-4 text-base-content/30"
                    />
                    <h3 className="text-lg font-semibold text-base-content mb-2">
                        Nenhuma notificação
                    </h3>
                    <p className="text-base-content/60 text-sm">
                        {hasFilters
                            ? 'Nenhuma notificação encontrada com os filtros aplicados'
                            : 'Você ainda não recebeu nenhuma notificação'}
                    </p>
                </div>
            ) : (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm overflow-hidden">
                    <div className="divide-y divide-base-300">
                        {groups.map(group => {
                            const meta = group.module
                                ? categoryLabels.get(group.module)
                                : undefined
                            return (
                                <NotificationGroupRow
                                    key={`${group.module || ''}|${
                                        group.resource || ''
                                    }|${group.title}`}
                                    group={group}
                                    categoryLabel={meta?.label}
                                    resourceLabel={
                                        group.resource
                                            ? meta?.resources.get(
                                                  group.resource
                                              )
                                            : undefined
                                    }
                                    onChanged={refresh}
                                />
                            )
                        })}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 p-4 border-t border-base-300">
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={page === 1}
                                onClick={() => setPage(prev => prev - 1)}
                            >
                                Anterior
                            </button>
                            <span className="text-sm text-base-content/60">
                                Página {page} de {totalPages}
                            </span>
                            <button
                                className="btn btn-sm btn-ghost"
                                disabled={page === totalPages}
                                onClick={() => setPage(prev => prev + 1)}
                            >
                                Próxima
                            </button>
                        </div>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteAllModal}
                onClose={() => setShowDeleteAllModal(false)}
                onConfirm={confirmDeleteAll}
                title="Limpar Notificações"
                message="Tem certeza que deseja excluir todas as notificações? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir Todas"
                loading={deleting}
            />
        </div>
    )
}
